'use client'

import React from 'react'
import Image from 'next/image'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { Flex } from '@quark/layout'
import { Text } from '@quark/typography'
import { Button } from '@quark/button'
import isMobile from 'ismobilejs'
import styles from './benefitsCard.module.scss'

enum ActionType {
  OpenModal = 'openModal',
  ExternalLink = 'externalLink',
}

const BenefitsCard = ({ data, setFormSnippet, setIsModalOpen }) => {
  const isMobileDevice = isMobile()?.phone

  const onActionTextClick = () => {
    if (data?.actionType === ActionType.ExternalLink) {
      window.open(data?.redirectionLink, '_blank')
      return
    }
    setIsModalOpen(true)
    setFormSnippet(data?.formSnippet)
  }

  return (
    <>
      <div className={styles.leftContent}>
        <Flex
          flexDirection="column"
          gap={{
            lg: 'spacing-32',
            md: 'spacing-24',
            sm: 'spacing-24',
          }}>
          <div className={styles.headerGroup}>
            <PropertyControlledComponent controllerProperty={data?.tagline} fallback={null}>
              <div className={styles.taglineWrapper}>
                <Text
                  as="span"
                  fontStyle={{
                    lg: 'um_text-base/normal',
                    md: 'um_text-base/normal',
                    sm: 'um_text-base/normal',
                  }}
                  color="base/primary">
                  <span dangerouslySetInnerHTML={{ __html: data?.tagline || '' }} />
                </Text>
              </div>
            </PropertyControlledComponent>
            <Text
              as="h2"
              fontStyle={{ lg: 'um_text-5xl/medium', md: 'um_text-3xl/medium', sm: 'um_text-2xl/medium' }}
              color="base/primary">
              <span dangerouslySetInnerHTML={{ __html: data?.title || '' }} />
            </Text>
          </div>
          <Flex
            flexDirection="column"
            gap={{
              lg: 'spacing-20',
              md: 'spacing-16',
              sm: 'spacing-16',
            }}>
            {data?.benefitList?.map((benefit, index) => {
              const iconSrc = typeof benefit?.iconUrl === 'string' ? benefit.iconUrl : benefit?.iconUrl?.url

              return (
                <div className={styles.benefitListItem} key={index}>
                  {iconSrc && (
                    <Image
                      src={iconSrc}
                      alt={benefit?.iconAlt || 'check-mark'}
                      width={isMobileDevice ? 16 : 24}
                      height={isMobileDevice ? 16 : 24}
                    />
                  )}
                  <Text
                    as="div"
                    fontStyle={{
                      lg: 'um_text-lg/normal',
                      md: 'um_text-lg/normal',
                      sm: 'um_text-lg/normal',
                    }}
                    color="base/primary">
                    <span dangerouslySetInnerHTML={{ __html: benefit?.title || '' }} />
                  </Text>
                </div>
              )
            })}
          </Flex>
          <PropertyControlledComponent controllerProperty={!isMobileDevice} fallback={null}>
            <Button type="solid" size="lg" onClick={onActionTextClick}>
              <Text fontStyle="um_text-lg/medium" color="base/inverted primary">
                {data?.actionText}
              </Text>
            </Button>
          </PropertyControlledComponent>
        </Flex>
        <div className={styles.buttonContainer}>
          <Button type="solid" size="lg" onClick={onActionTextClick}>
            <Text fontStyle="um_text-lg/medium" color="base/inverted primary">
              {data?.actionText}
            </Text>
          </Button>
        </div>
      </div>
      <div className={styles.rightContent}>
        <PropertyControlledComponent controllerProperty={data?.heroImage?.url} fallback={null}>
          <Image
            src={data?.heroImage?.url}
            alt={data?.heroImageAlt || 'logo'}
            width={400}
            height={303}
            sizes="(max-width: 820px) 304px, 400px"
            priority
            className={styles.heroImage}
            id="spline-end-position"
          />
        </PropertyControlledComponent>
        <div className={styles.floatingImages}>
          {data?.floatingImages?.map((image, index) => (
            <Image
              key={image?.url || index}
              src={image?.url}
              alt=""
              width={isMobileDevice ? 160 : 210}
              height={isMobileDevice ? 160 : 210}
              className={styles.floatingImageItem}
            />
          ))}
        </div>
      </div>
    </>
  )
}

export default BenefitsCard
